import React, { FC, PropsWithChildren, useState, useRef, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import View from '../view';
import { requestAnimationFrame } from '../../common/utils';

export type ITransitionNameType = 'fade' | 'fade-up' | 'fade-down' | 'fade-left' | 'fade-right' | 'slide-up' | 'slide-down' | 'slide-left' | 'slide-right';

interface ITransitionProps {
  /**
   * css class
   */
  className?: string;
  /**
   * 动画名称
   * @default fade
   */
  name?: ITransitionNameType;
  /**
   * 动画时长(ms)
   * @default 300
   */
  duration?: number;
  /**
   * 是否展示组件
   * @default true
   */
  visible?: boolean;
  /**
   * 点击事件回调函数
   */
  onClick?: () => void;
}

const getClassNames = (name: ITransitionNameType) => ({
  enter: `ink-transition__${name}-enter ink-transition__${name}-enter--active`,
  'enter-to': `ink-transition__${name}-enter-to ink-transition__${name}-enter--active`,
  leave: `ink-transition__${name}-leave ink-transition__${name}-leave--active`,
  'leave-to': `ink-transition__${name}-leave-to ink-transition__${name}-leave--active`,
});

const Transition: FC<PropsWithChildren<ITransitionProps>> = ({ children, className, name = 'fade', duration = 300, visible = true, onClick }) => {
  const [inited, setInited] = useState(false);
  const [display, setDisplay] = useState(false);
  const [classes, setClasses] = useState('');
  const delayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const statusRef = useRef<'enter' | 'leave'>('enter');
  const transitionEndedRef = useRef<boolean>(false);
  const rootClass = classNames('ink-transition', className);

  const cleanDelayTimer = () => {
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
  };

  const onTransitionEnd = useCallback(() => {
    if (transitionEndedRef.current) {
      return;
    }
    transitionEndedRef.current = true;
    if (!visible && display) {
      setDisplay(false);
      setInited(false);
    }
  }, [visible, display]);

  const enter = useCallback(async () => {
    cleanDelayTimer();

    const classNamesObj = getClassNames(name);
    statusRef.current = 'enter';

    setInited(true);
    setDisplay(true);
    setClasses(classNamesObj.enter);

    await requestAnimationFrame();

    transitionEndedRef.current = false;
    setClasses(classNamesObj['enter-to']);
  }, [name]);

  const leave = useCallback(async () => {
    if (!display) {
      return;
    }
    cleanDelayTimer();

    const classNamesObj = getClassNames(name);
    statusRef.current = 'leave';

    setClasses(classNamesObj.leave);

    await requestAnimationFrame();

    transitionEndedRef.current = false;
    setClasses(classNamesObj['leave-to']);
  }, [name, display]);

  useEffect(() => {
    if (visible) {
      enter();
    } else {
      leave();
    }
    return () => {
      cleanDelayTimer();
    };
  }, [visible, enter, leave]);

  return inited ? (
    <View
      className={`${rootClass} ${classes}`}
      style={{
        transitionDuration: `${duration}ms`,
        ...(!display ? { display: 'none' } : {}),
      }}
      onTransitionEnd={onTransitionEnd}
      onClick={onClick}
    >
      {children}
    </View>
  ) : null;
};

export default Transition;
